import React, { useEffect, useState } from 'react';
import Navbar from '../components/layout/Navbar';
import Footer from '../components/layout/Footer';
import { Link } from 'react-router-dom';
import { Camera, Hash, Share2, Loader2 } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AnniversaryPage = () => {
  const [content, setContent] = useState(null);
  const [loading, setLoading] = useState(true);

  // Load Typeform embed script and fetch content
  useEffect(() => {
    const script = document.createElement('script');
    script.src = '//embed.typeform.com/next/embed.js';
    script.async = true;
    document.body.appendChild(script);

    const fetchContent = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/special-events-content`);
        setContent(response.data);
      } catch (err) {
        console.error('Error fetching special events content:', err);
        // Use defaults
        setContent({});
      } finally {
        setLoading(false);
      }
    };
    fetchContent();
    
    return () => {
      const existingScript = document.querySelector('script[src="//embed.typeform.com/next/embed.js"]');
      if (existingScript) {
        existingScript.remove();
      }
    };
  }, []);

  // Default values
  const c = {
    hero_label: content?.hero_label || "February 27th • 10th Anniversary",
    hero_title: content?.hero_title || "Special Events",
    hero_description: content?.hero_description || "Every February 27th, we come together to honor the memory of Leonard Nimoy and celebrate the enduring message of hope, logic, and compassion he gave the world.",
    hero_hashtag_text: content?.hero_hashtag_text || "Share your photos and Vulcan Salute with #LLAPTribute and #RememberingLeonardNimoy to participate.",
    hero_tagline: content?.hero_tagline || "Live Long and Prosper",
    gift_label: content?.gift_label || "Special 10th Anniversary Gift",
    gift_title: content?.gift_title || "Receive a Gift from The Nimoy-Knight Foundation",
    gift_description: content?.gift_description || "To receive a special gift from The Nimoy-Knight Foundation, make a donation of $100 or more and fill out the form below.",
    gift_button_text: content?.gift_button_text || "Get Started",
    gift_button_link: content?.gift_button_link || "/donate",
    gift_typeform_id: content?.gift_typeform_id || "01KETY7YEZQAK5HEY3QMQR31CD",
    remember_label: content?.remember_label || "Remembering Leonard",
    remember_title: content?.remember_title || "A Day of Celebration",
    remember_description: content?.remember_description || "On February 27, 2015, the world lost a legend. But Leonard Nimoy's spirit lives on through the millions of fans he inspired.",
    remember_card1_title: content?.remember_card1_title || "His Legacy Lives On",
    remember_card1_text: content?.remember_card1_text || "Leonard touched countless lives through his portrayal of Spock, his photography, poetry, and his unwavering humanity.",
    remember_card2_title: content?.remember_card2_title || "The Vulcan Salute",
    remember_card2_text: content?.remember_card2_text || "The iconic hand gesture Leonard created has become a universal symbol of goodwill.",
    share_label: content?.share_label || "Be Part of the Tribute",
    share_title: content?.share_title || "Share Your Vulcan Salute",
    share_description: content?.share_description || "Join fans worldwide in honoring Leonard Nimoy. Take a photo doing the Vulcan salute and share it on social media.",
    share_step1_title: content?.share_step1_title || "1. Take a Photo",
    share_step1_text: content?.share_step1_text || "Capture yourself doing the iconic Vulcan salute 🖖",
    share_step2_title: content?.share_step2_title || "2. Add Hashtags",
    share_step2_text: content?.share_step2_text || "Include #LLAP and #RememberingLeonardNimoy",
    share_step3_title: content?.share_step3_title || "3. Share & Inspire",
    share_step3_text: content?.share_step3_text || "Post to your favorite social platform",
    share_hashtag1: content?.share_hashtag1 || "#LLAP",
    share_hashtag2: content?.share_hashtag2 || "#RememberingLeonardNimoy",
    share_hashtag3: content?.share_hashtag3 || "#llaptribute",
    quote_text: content?.quote_text || "A life is like a garden. Perfect moments can be had, but not preserved, except in memory. LLAP",
    quote_attribution: content?.quote_attribution || "Leonard Nimoy's final tweet, February 23, 2015",
    cta_title: content?.cta_title || "Keep His Memory Alive",
    cta_description: content?.cta_description || "Support the Nimoy-Knight Foundation and help us continue Leonard's legacy of hope, compassion, and inspiration for all.",
    cta_button_text: content?.cta_button_text || "Support the Foundation",
    cta_button_link: content?.cta_button_link || "/donate",
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a0a0b] flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-amber-500 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a0a0b]">
      <Navbar />
      
      {/* Hero Section */}
      <section className="pt-32 pb-16 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-amber-500/5 via-transparent to-transparent" />
        
        <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-amber-500 text-sm font-semibold tracking-widest uppercase mb-4">
            {c.hero_label}
          </p>
          <h1 className="text-4xl md:text-6xl font-bold text-white mb-6">
            {c.hero_title}
          </h1>
          <p className="text-gray-300 text-lg leading-relaxed mb-8">
            {c.hero_description}
          </p>
          <p className="text-gray-400 text-sm mb-8">
            {c.hero_hashtag_text}
          </p>
          <div className="text-6xl mb-4">🖖</div>
          <p className="text-amber-500 font-semibold">{c.hero_tagline}</p>
        </div>
      </section>

      {/* Gift Section */}
      <section className="py-24 bg-[#0f0f12]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <span className="text-amber-500 text-sm font-semibold tracking-widest uppercase">
            {c.gift_label}
          </span>
          <h2 className="text-3xl font-bold text-white mt-4 mb-6">
            {c.gift_title}
          </h2>
          <p className="text-gray-400 text-lg mb-10">
            {c.gift_description}
          </p>
          
          {/* Typeform Embed */}
          <div className="mb-10">
            <div data-tf-live={c.gift_typeform_id}></div>
          </div>
          
          <Link
            to={c.gift_button_link}
            className="inline-flex items-center justify-center px-10 py-4 bg-amber-500 hover:bg-amber-600 text-black font-semibold rounded-full transition-all hover:scale-105 text-lg"
          >
            {c.gift_button_text}
          </Link>
        </div>
      </section>

      {/* Remembering Section */}
      <section className="py-24 bg-[#0a0a0b]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="text-amber-500 text-sm font-semibold tracking-widest uppercase">
              {c.remember_label}
            </span>
            <h2 className="text-4xl font-bold text-white mt-4">
              {c.remember_title}
            </h2>
          </div>

          <p className="text-gray-400 text-lg text-center mb-12">
            {c.remember_description}
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="bg-[#141416] border border-gray-800/50 rounded-2xl p-8">
              <h3 className="text-xl font-bold text-white mb-4">{c.remember_card1_title}</h3>
              <p className="text-gray-400 leading-relaxed">
                {c.remember_card1_text}
              </p>
              <div className="text-4xl mt-6">🖖</div>
            </div>
            <div className="bg-[#141416] border border-gray-800/50 rounded-2xl p-8">
              <h3 className="text-xl font-bold text-white mb-4">{c.remember_card2_title}</h3>
              <p className="text-gray-400 leading-relaxed">
                {c.remember_card2_text}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Share Section */}
      <section className="py-24 bg-[#0f0f12]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <span className="text-amber-500 text-sm font-semibold tracking-widest uppercase">
            {c.share_label}
          </span>
          <h2 className="text-3xl font-bold text-white mt-4 mb-6">
            {c.share_title}
          </h2>
          <p className="text-gray-400 text-lg mb-12">
            {c.share_description}
          </p>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
            <div className="bg-[#141416] border border-gray-800/50 rounded-2xl p-6">
              <div className="p-4 bg-amber-500/10 rounded-xl w-fit mx-auto mb-4">
                <Camera className="w-8 h-8 text-amber-500" />
              </div>
              <h3 className="text-lg font-bold text-white mb-2">{c.share_step1_title}</h3>
              <p className="text-gray-400 text-sm">
                {c.share_step1_text}
              </p>
            </div>
            <div className="bg-[#141416] border border-gray-800/50 rounded-2xl p-6">
              <div className="p-4 bg-amber-500/10 rounded-xl w-fit mx-auto mb-4">
                <Hash className="w-8 h-8 text-amber-500" />
              </div>
              <h3 className="text-lg font-bold text-white mb-2">{c.share_step2_title}</h3>
              <p className="text-gray-400 text-sm">
                {c.share_step2_text}
              </p>
            </div>
            <div className="bg-[#141416] border border-gray-800/50 rounded-2xl p-6">
              <div className="p-4 bg-amber-500/10 rounded-xl w-fit mx-auto mb-4">
                <Share2 className="w-8 h-8 text-amber-500" />
              </div>
              <h3 className="text-lg font-bold text-white mb-2">{c.share_step3_title}</h3>
              <p className="text-gray-400 text-sm">
                {c.share_step3_text}
              </p>
            </div>
          </div>

          <div className="flex flex-wrap justify-center gap-4">
            <span className="px-6 py-3 bg-[#1a1a1c] border border-gray-800 rounded-full text-amber-500 font-semibold">
              {c.share_hashtag1}
            </span>
            <span className="px-6 py-3 bg-[#1a1a1c] border border-gray-800 rounded-full text-amber-500 font-semibold">
              {c.share_hashtag2}
            </span>
            <span className="px-6 py-3 bg-[#1a1a1c] border border-gray-800 rounded-full text-amber-500 font-semibold">
              {c.share_hashtag3}
            </span>
          </div>
        </div>
      </section>

      {/* Final Quote */}
      <section className="py-24 bg-[#0a0a0b]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="text-amber-500 text-6xl font-serif mb-6">"</div>
          <blockquote className="text-xl md:text-2xl font-light text-white leading-relaxed mb-8">
            {c.quote_text}
          </blockquote>
          <p className="text-amber-500 text-lg">
            — <span className="italic">{c.quote_attribution}</span>
          </p>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-[#0f0f12]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-white mb-6">
            {c.cta_title}
          </h2>
          <p className="text-gray-400 text-lg mb-10">
            {c.cta_description}
          </p>
          <Link
            to={c.cta_button_link}
            className="inline-flex items-center justify-center px-10 py-4 bg-amber-500 hover:bg-amber-600 text-black font-semibold rounded-full transition-all hover:scale-105 text-lg"
          >
            {c.cta_button_text}
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default AnniversaryPage;
